import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { withRouterProps } from '../../utils/with-router-props'
import { callApi, fetchApiDataIfNeeded } from '../../../actions/api'
import SHSForm from '../../utils/shs-form'
import VerifyPassword from '../../shared/forms/verify-password'
import { apiResourceEndpoint, buildRoutePath, routes } from '../../../constants/routes'
import EditableDisplayField from '../../utils/editable-display-field'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'

// Team manager viewing user profile
const mapStateToProps = (state, ownProps) => {
    return {
        roles: state.roles,
        user: state.members.find(u => u.id === Number(ownProps.params.userId)),
        account: state.account,
        forms: state.forms
    }
}

const TeamMemberView = ({ dispatch, params, navigate, user, roles, account, forms }) => {
    const userId = params.userId
    const [inEdit, setInEdit] = useState(false)
    const [allValid, setAllValid] = useState(false)

    const handleValidationChange = (isValid) => {
        setAllValid(isValid)
    }

    useEffect(() => {
        if (userId) {
            dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('members', 'VIEW', userId)))
        }
    }, [dispatch, userId])

    useEffect(() => {
        dispatch(fetchApiDataIfNeeded(apiResourceEndpoint('roles', 'LIST')))
    }, [dispatch])

    const roleOptions = roles.slice()
        .sort((a, b) => {
            if (a.level > b.level) {
                return -1
            }
            return 1
        })
        .map(role => ({ value: role.id, label: role.name }))

    const handleDelete = () => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            dispatch(callApi(apiResourceEndpoint('members', 'DELETE', userId), {}, -1))
                .then((response) => {
                    if (response === 'SUCCESS') {
                        navigate(buildRoutePath(routes.appTeamIndex.path, { accountId: account.id }))
                    } else {
                        window.alert('Unable to delete user. Please try again.')
                    }
                })
        }
    }

    const successFX = () => {
        setInEdit(false)
        window.alert('Password updated successfully!')
    }

    const submitFX = (formData, formId) => {
        dispatch(callApi(apiResourceEndpoint('members', 'UPDATE', user.id, 'password'), formData, formId))
    }

    const renderPasswordForm = () => {
        return (
            <SHSForm submitFX={submitFX} successFX={successFX} buttonLabel="Update Password" buttonDisabled={!allValid} showCancel={true} cancelAction={() => setInEdit(false)}>
                <VerifyPassword onValidationChange={handleValidationChange} />
            </SHSForm>
        )
    }

    const renderPasswordView = () => {
        return (
            <>
                <Link onClick={() => setInEdit(true)}>Update Password <FontAwesomeIcon icon="caret-down" /></Link>
            </>
        )
    }

    const renderPasswordChange = () => {
        return (
            <div className="clickable-right">
                {inEdit ? renderPasswordForm() : renderPasswordView()}
            </div>
        )
    }

    if (!user) {
        return <div className="error">No User Found</div>
    }

    const { firstName, lastName, email, phone } = user

    return (
        <div className="app-dashboard">
            <div className="header">
                <div className="container">
                    <div className="header-details">
                        <h1>{firstName} {lastName}'s Profile</h1>
                        <p>{ user._computed.accountTypeName }</p>
                    </div>
                </div>
            </div>
            <div className="content-wrapper">
                <div className="container">
                    <div className="content-box editable">
                        <span>
                            Click on any field to edit.
                        </span>
                        <hr/>
                        <EditableDisplayField
                            label="First Name"
                            display={firstName}
                            extraClass="flex-33"
                            endpoint="members"
                            dataObj={user}
                            inTable={true}
                            fields={[{
                                field: 'firstName',
                                type: 'text',
                                required: true
                            }]} />
                        <EditableDisplayField
                            label="Last Name"
                            display={lastName}
                            extraClass="flex-33"
                            endpoint="members"
                            dataObj={user}
                            inTable={true}
                            fields={[{
                                field: 'lastName',
                                type: 'text',
                                required: true
                            }]} />
                        <EditableDisplayField
                            label="Email"
                            display={email}
                            extraClass="flex-33"
                            endpoint="members"
                            dataObj={user}
                            inTable={true}
                            fields={[{
                                field: 'email',
                                type: 'text',
                                required: true
                            }]} />
                        <EditableDisplayField
                            label="Phone"
                            extraClass="flex-33"
                            display={phone}
                            endpoint="members"
                            dataObj={user}
                            inTable={true}
                            fields={[{
                                field: 'phone',
                                type: 'text',
                                required: true
                            }]} />
                        <div className="label-values flex-33">
                            <span className="label">Account</span>
                            <p className="values">{user._computed.accountTypeName}</p>
                        </div>
                        <EditableDisplayField
                            label="Role"
                            display={user._computed.roleName}
                            extraClass="flex-33"
                            endpoint="members"
                            dataObj={user}
                            inTable={true}
                            fields={[{
                                field: 'roleId',
                                type: 'valueSelect',
                                options: roleOptions,
                                required: true
                            }]} />
                    </div>
                </div>
                <div className="container">
                    <div className="content-box">
                        { user.createdAt ?
                            <>
                                <h3>Auth Details</h3>
                                <p>
                                    <span className="p-label">Created At</span><br />
                                    {moment(user.createdAt).format('ddd. MMM. Do Y, hh:mm a')}<br />
                                    <span className="sm-txt">{moment(user.createdAt).fromNow()}</span>
                                </p>
                                <p>
                                    <span className="p-label">Last Updated</span><br />
                                    {user.updatedAt ? moment(user.updatedAt).format('ddd. MMM. Do Y, hh:mm a') : 'N/A'}<br />
                                    {user.updatedAt && <span className="sm-txt">{moment(user.updatedAt).fromNow()}</span>}
                                </p>
                            </>
                            : <p>User has never logged in</p>
                        }
                        { renderPasswordChange() }
                    </div>
                </div>
                <div className="container">
                    <div className="content-box delete-wrapper">
                        <div className="btn small red" onClick={handleDelete}>
                            DELETE USER <FontAwesomeIcon icon="trash-alt" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(TeamMemberView))
